/**
 * Shared HTML sanitizer for every raw-HTML render sink (security findings #6/#9).
 *
 * NOS stores HTML from two untrusted-ish sources — imported third-party sites
 * and org-member rich text — and renders it on public pages via
 * dangerouslySetInnerHTML. Everything that reaches such a sink must pass
 * through sanitizeHtml(); hrefs bound directly to <a> elements go through
 * safeHref().
 *
 * dompurify is pinned to exactly 3.3.1: 3.4.x fails to strip <script> under
 * happy-dom (the unit-test DOM), which would leave the sanitizer untestable.
 * Before bumping the pin, re-run src/lib/__tests__/sanitizeHtml.test.ts.
 */
import DOMPurify from "dompurify"

/**
 * Strict allowlist for imported/rich-text prose. `div` is included because
 * contenteditable (RichTextInput) represents line breaks as <div> wrappers,
 * and `hr` because tiptapJsonToHtml emits it for horizontalRule — both carry
 * no allowed attributes, so they add no script surface.
 */
const ALLOWED_TAGS = [
  "p", "br", "strong", "b", "em", "i", "u", "s", "a",
  "ul", "ol", "li",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "blockquote", "code", "pre", "span", "div", "hr",
]

const ALLOWED_ATTR = ["href", "title", "target", "rel"]

let hookRegistered = false

function registerHooks(): void {
  if (hookRegistered) return
  hookRegistered = true
  // Links that keep target (e.g. target="_blank") must not leak window.opener.
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A" && node.getAttribute("target")) {
      node.setAttribute("rel", "noopener noreferrer")
    }
  })
}

/** Last-resort fallback for DOM-less environments: strip all tags. */
function stripTags(dirty: string): string {
  return dirty.replace(/<[^>]*>/g, "")
}

/**
 * Sanitize untrusted HTML for rendering via dangerouslySetInnerHTML.
 * Keeps benign prose markup, drops scripts, event handlers, style, and
 * javascript: URLs (DOMPurify's default ALLOWED_URI_REGEXP is kept as-is).
 *
 * Sanitization loops until the output is a fixed point. In browsers the second
 * pass is an idempotent no-op; under non-browser DOMs (happy-dom in unit
 * tests) DOMPurify's single pass can miss children re-inserted while removing
 * their parent, and the extra passes converge to fully-clean output.
 */
export function sanitizeHtml(dirty: string): string {
  if (typeof dirty !== "string" || dirty === "") return ""
  if (!DOMPurify.isSupported) return stripTags(dirty)
  registerHooks()
  let out = dirty
  for (let i = 0; i < 10; i++) {
    const prev = out
    out = DOMPurify.sanitize(prev, { ALLOWED_TAGS, ALLOWED_ATTR })
    if (out === prev) return out
  }
  // No fixed point reached — refuse to emit potentially unstable markup.
  return stripTags(out)
}

/**
 * Scheme guard for hrefs bound directly into <a href={...}>.
 * Allows http(s), mailto, tel, root-relative ("/…", but not
 * protocol-relative "//…") and same-page anchors ("#…"). Anything else
 * (javascript:, data:, vbscript:, bare relatives) returns undefined so the
 * anchor renders without a live href.
 */
export function safeHref(url?: string): string | undefined {
  if (typeof url !== "string") return undefined
  const trimmed = url.trim()
  if (!trimmed) return undefined
  if (trimmed.startsWith("#")) return trimmed
  if (trimmed.startsWith("/") && !trimmed.startsWith("//")) return trimmed
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return trimmed
  return undefined
}
